(() => {
  if (customElements.get('bc-growth-system')) return;
  class BlackCometGrowthSystem extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (node, type, fn, options = {}) => node.addEventListener(type, fn, { ...options, signal: this.controller.signal });
      this.network = this.querySelector('[data-system-network]');
      this.hub = this.querySelector('[data-system-hub]');
      this.core = this.querySelector('[data-system-core]');
      this.symbol = this.querySelector('[data-system-symbol-art]');
      this.chargeRing = this.querySelector('[data-system-charge]');
      this.chargeLevel = 0;
      this.chargeCompletedAt = null;
      this.completionPlayed = false;
      this.wires = this.querySelector('[data-system-wires]');
      this.cards = [...this.querySelectorAll('[data-system-card]')];
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 900px)');
      this.cards.forEach(card => {
        const detail = card.querySelector('[data-system-detail]');
        detail.inert = true;
        detail.setAttribute('aria-hidden', 'true');
        const icon = card.querySelector('[data-system-icon]');
        on(icon, 'ready', () => this.updateIcon(card, card.classList.contains('is-open')));
        if (icon.playerInstance?.isReady) this.updateIcon(card, false);
      });
      if (!customElements.get('lord-icon') && !document.querySelector('script[data-bc-lordicon-player]')) {
        const loader = document.createElement('script');
        loader.src = this.dataset.iconPlayer; loader.async = true;
        loader.setAttribute('data-bc-lordicon-player', '');
        document.head.append(loader);
      }
      this.elapsed = 0;
      const ns = 'http://www.w3.org/2000/svg';
      const definitions = document.createElementNS(ns, 'defs');
      this.wires.append(definitions);
      const gradientPrefix = `bc-system-trail-${this.querySelector('.bc-system__heading').id}`;
      const packets = document.createElementNS(ns, 'g');
      packets.setAttribute('class', 'bc-system__packets');
      this.connections = this.cards.map((card, index) => {
        const group = document.createElementNS(ns, 'g'); group.setAttribute('class', 'bc-system__connection');
        const line = document.createElementNS(ns, 'path'); line.setAttribute('class', 'bc-system__wire');
        const trail = document.createElementNS(ns, 'path'); trail.setAttribute('class', 'bc-system__wire-trail'); trail.setAttribute('pathLength', '1');
        const gradient = document.createElementNS(ns, 'linearGradient');
        gradient.setAttribute('id', `${gradientPrefix}-${index}`);
        gradient.setAttribute('gradientUnits', 'userSpaceOnUse');
        [[0, 0], [.45, .2], [1, 1]].forEach(([offset, opacity]) => {
          const stop = document.createElementNS(ns, 'stop');
          stop.setAttribute('class', 'bc-system__trail-stop');
          stop.setAttribute('offset', String(offset)); stop.setAttribute('stop-opacity', String(opacity));
          gradient.append(stop);
        });
        definitions.append(gradient);
        trail.setAttribute('stroke', `url(#${gradientPrefix}-${index})`);
        const dot = document.createElementNS(ns, 'circle'); dot.setAttribute('class', 'bc-system__wire-dot'); dot.setAttribute('r', '2.2');
        group.append(line, trail); packets.append(dot); this.wires.append(group);
        const button = card.querySelector('[data-system-toggle]');
        on(card, 'pointerenter', event => { if (event.pointerType === 'mouse' && !this.mobile.matches) this.openCard(index); });
        on(card, 'pointerleave', event => {
          if (event.pointerType !== 'mouse' || card.contains(document.activeElement)) return;
          this.closeTimer = setTimeout(() => { if (this.activeIndex === index) this.openCard(null); }, 180);
        });
        on(card, 'focusin', event => { if (event.target !== button) this.openCard(index); });
        on(card, 'focusout', () => {
          this.focusTimer = setTimeout(() => { if (!this.cards.some(item => item.contains(document.activeElement))) this.openCard(null); }, 0);
        });
        on(button, 'click', event => {
          const next = this.activeIndex === index && (event.detail === 0 || this.mobile.matches || event.target.closest('.bc-system__plus')) ? null : index;
          this.openCard(next);
        });
        return { card, group, line, trail, gradient, dot, delay: index * 550, arrivalCycle: -1 };
      });
      // SVG paints later children on top: every packet stays above every wire.
      this.wires.append(packets);
      this.setCharge(0);
      this.restartButton = this.querySelector('[data-system-restart]');
      on(this.restartButton, 'click', () => {
        this.restartCycle();
        this.sync();
        this.restartSpin?.cancel();
        if (!this.reduced.matches) this.restartSpin = this.restartButton.querySelector('svg').animate([
          { transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }
        ], { duration: 500, easing: 'cubic-bezier(.22,1,.36,1)' });
      });
      on(this, 'keydown', event => {
        if (event.key === 'Escape' && this.activeIndex !== null && this.activeIndex !== undefined) {
          event.preventDefault(); const index = this.activeIndex;
          this.ignoreFocus = true; this.cards[index].querySelector('[data-system-toggle]').focus({ preventScroll: true });
          this.ignoreFocus = false; this.openCard(null);
        }
      });
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.openCard(null); });
      on(document, 'visibilitychange', () => this.sync());
      on(this.reduced, 'change', () => this.sync());
      on(this.mobile, 'change', () => this.layout());
      on(document, 'shopify:block:select', event => {
        const index = this.cards.findIndex(card => card.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.openCard(index);
      });
      this.resize = new ResizeObserver(() => this.layout());
      this.resize.observe(this.network); this.cards.forEach(card => this.resize.observe(card));
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.classList.add('is-visible');
        this.sync();
      }, { threshold: .12 });
      this.observer.observe(this);
      this.step = this.step.bind(this);
      this.layout();
    }
    openCard(index) {
      if (this.ignoreFocus) return;
      clearTimeout(this.closeTimer); clearTimeout(this.focusTimer);
      this.activeIndex = index;
      this.cards.forEach((card, i) => {
        const open = i === index;
        const changed = card.classList.contains('is-open') !== open;
        card.classList.toggle('is-open', open);
        if (changed) this.updateIcon(card, open);
        card.querySelector('[data-system-toggle]').setAttribute('aria-expanded', String(open));
        const detail = card.querySelector('[data-system-detail]'); detail.inert = !open; detail.setAttribute('aria-hidden', String(!open));
        this.connections[i].group.classList.toggle('is-selected', open);
        this.connections[i].dot.classList.toggle('is-selected', open);
      });
      this.querySelectorAll('[data-system-stage]').forEach(stage => stage.classList.toggle('is-selected', this.cards[index]?.dataset.stage === stage.dataset.systemStage));
    }
    updateIcon(card, play = false) {
      const icon = card.querySelector('[data-system-icon]');
      const player = icon?.playerInstance;
      if (!player?.isReady) return;
      player.loop = false;
      player.pause();
      icon.parentElement.classList.add('is-ready');
      if (play && !this.reduced.matches && !document.hidden) player.playFromBeginning();
      else player.goToLastFrame();
    }
    layout() {
      const box = this.network.getBoundingClientRect();
      const core = this.core.getBoundingClientRect();
      const hub = this.hub.getBoundingClientRect();
      this.wires.setAttribute('viewBox', `0 0 ${box.width} ${box.height}`);
      this.connections.forEach(({ card, line, trail }, index) => {
        // Layout coordinates stay stable while the entrance transform animates.
        const rect = { left: box.left + card.offsetLeft, right: box.left + card.offsetLeft + card.offsetWidth, top: box.top + card.offsetTop, width: card.offsetWidth, height: card.offsetHeight };
        const toggle = card.querySelector('[data-system-toggle]');
        const detail = card.querySelector('[data-system-detail]');
        const detailInner = detail.querySelector('.bc-system__detail-inner');
        const reveal = Math.min(1, detail.offsetHeight / Math.max(1, detailInner.offsetHeight));
        const toggleTop = rect.top + card.clientTop + toggle.offsetTop - box.top;
        // Follow the opening panel smoothly, then meet its divider exactly.
        const y = toggleTop + toggle.offsetHeight * (.5 + .5 * reveal);
        let d;
        if (this.mobile.matches) {
          const left = rect.left + rect.width / 2 < box.left + box.width / 2;
          const x = (left ? rect.right : rect.left) - box.left;
          const center = core.left + core.width / 2 - box.left;
          const end = core.bottom - box.top - 4;
          d = `M${x},${y}H${center}V${end}`;
        } else {
          const left = card.dataset.side === 'left';
          const x = (left ? rect.right : rect.left) - box.left;
          const row = index % 3;
          const radius = hub.width * 117 / 260;
          const offsetY = (row - 1) * radius * .58;
          const offsetX = Math.sqrt(radius * radius - offsetY * offsetY);
          const endX = hub.left + hub.width / 2 + (left ? -offsetX : offsetX) - box.left;
          const endY = hub.top + hub.height / 2 + offsetY - box.top;
          const middle = (x + endX) / 2;
          d = `M${x},${y}C${middle},${y} ${middle},${endY} ${endX},${endY}`;
        }
        line.setAttribute('d', d); trail.setAttribute('d', d);
        this.connections[index].length = line.getTotalLength();
      });
    }
    sync() {
      const moving = this.visible && !document.hidden && !this.reduced.matches;
      if (!moving) this.cards.forEach(card => this.updateIcon(card));
      this.classList.toggle('is-motion-active', moving);
      if (this.reduced.matches) this.classList.add('is-visible');
      if (!moving) {
        cancelAnimationFrame(this.frame); this.frame = null; this.lastTime = null;
        this.corePulse?.cancel(); this.restartSpin?.cancel();
        this.hub.classList.remove('is-completion-pulse');
        if (this.reduced.matches) {
          this.connections.forEach(({ trail, dot }) => { trail.style.opacity = dot.style.opacity = '0'; });
          this.setCharge(this.connections.length * 2);
        }
      } else if (!this.frame) this.frame = requestAnimationFrame(this.step);
    }
    step(time) {
      if (this.lastTime != null) this.elapsed += Math.min(80, time - this.lastTime);
      this.lastTime = time;
      if (this.chargeCompletedAt != null && this.elapsed - this.chargeCompletedAt >= 10000) this.restartCycle();
      this.connections.forEach(connection => {
        const { delay, line, trail, gradient, dot, length } = connection;
        const travel = this.elapsed - delay;
        const age = travel % 6200;
        const cycle = Math.floor(travel / 6200);
        if (age >= 2600 && cycle >= 0 && connection.arrivalCycle !== cycle) {
          connection.arrivalCycle = cycle;
          this.pulseCore();
        }
        const active = age >= 0 && age < 2600 && length > 0;
        if (!active) { trail.style.opacity = dot.style.opacity = '0'; return; }
        const progress = age / 2600;
        const tailStart = Math.max(0, progress - .18);
        const alpha = Math.min(age / 250, (2600 - age) / 250, 1);
        const point = line.getPointAtLength(progress * length);
        const tailPoint = line.getPointAtLength(tailStart * length);
        gradient.setAttribute('x1', tailPoint.x); gradient.setAttribute('y1', tailPoint.y);
        gradient.setAttribute('x2', point.x); gradient.setAttribute('y2', point.y);
        dot.setAttribute('cx', point.x); dot.setAttribute('cy', point.y); dot.style.opacity = String(alpha * .8);
        trail.style.strokeDasharray = `${Math.min(.18, progress)} 1`;
        trail.style.strokeDashoffset = String(-tailStart);
        trail.style.opacity = String(alpha * .5);
      });
      if (this.chargeCompletedAt != null && this.elapsed >= this.chargeCompletedAt && !this.completionPlayed) this.pulseCompletion();
      this.frame = requestAnimationFrame(this.step);
    }
    pulseCore() {
      if (!this.symbol || this.reduced.matches) return;
      if (this.chargeLevel >= this.connections.length * 2) return;
      this.corePulse?.cancel();
      this.hub.dataset.arrivals = String((Number(this.hub.dataset.arrivals) || 0) + 1);
      this.setCharge(this.chargeLevel + 1);
      this.corePulse = this.symbol.animate([
        { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 #7cffcb00)' },
        { transform: 'scale(1.13)', filter: 'drop-shadow(0 0 9px #7cffcb60)', offset: .28 },
        { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 #7cffcb00)' }
      ], { duration: 520, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
    pulseCompletion() {
      if (this.reduced.matches || !this.symbol || this.completionPlayed) return;
      this.completionPlayed = true;
      this.hub.dataset.completionPulses = String((Number(this.hub.dataset.completionPulses) || 0) + 1);
      this.hub.classList.add('is-completion-pulse');
      this.corePulse?.cancel();
      this.corePulse = this.symbol.animate([
        { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 #7cffcb00)' },
        { transform: 'scale(1.27)', filter: 'drop-shadow(0 0 16px #7cffcb99)', offset: .22 },
        { transform: 'scale(.96)', filter: 'drop-shadow(0 0 5px #7cffcb40)', offset: .4 },
        { transform: 'scale(1.13)', filter: 'drop-shadow(0 0 11px #7cffcb75)', offset: .57 },
        { transform: 'scale(1)', filter: 'drop-shadow(0 0 4px #7cffcb30)', offset: .8 },
        { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 #7cffcb00)' }
      ], { duration: 1450, easing: 'cubic-bezier(.22,1,.36,1)' });
    }
    setCharge(level) {
      const total = this.connections.length * 2;
      const previous = this.chargeLevel;
      this.chargeLevel = Math.min(total, Math.max(0, level));
      this.hub.dataset.charge = String(this.chargeLevel);
      this.hub.dataset.chargeTotal = String(total);
      this.hub.dataset.charged = String(total > 0 && this.chargeLevel === total);
      this.chargeRing.style.strokeDashoffset = String(total ? 1 - this.chargeLevel / total : 1);
      // SVG units: the soft fill is 125% of the 699-unit isotype height.
      this.hub.style.setProperty('--bc-system-fill-shift', `${total ? 873.75 * (1 - this.chargeLevel / total) : 873.75}px`);
      // Allow 500 ms for the last fill/arc, then hold the completed state for 10 s.
      if (this.chargeLevel === total && previous < total) this.chargeCompletedAt = this.elapsed + 500;
      else if (this.chargeLevel < total) {
        this.chargeCompletedAt = null;
        this.completionPlayed = false;
        this.hub.classList.remove('is-completion-pulse');
      }
    }
    restartCycle() {
      this.corePulse?.cancel();
      this.elapsed = 0;
      this.lastTime = null;
      this.connections.forEach(connection => {
        connection.arrivalCycle = -1;
        connection.trail.style.opacity = connection.dot.style.opacity = '0';
      });
      this.setCharge(0);
      this.hub.dataset.cycle = String((Number(this.hub.dataset.cycle) || 0) + 1);
    }
    disconnectedCallback() {
      this.controller?.abort(); this.resize?.disconnect(); this.observer?.disconnect();
      cancelAnimationFrame(this.frame); this.frame = null; this.lastTime = null;
      this.corePulse?.cancel(); this.restartSpin?.cancel(); clearTimeout(this.closeTimer); clearTimeout(this.focusTimer);
      this.cards?.forEach(card => this.updateIcon(card));
      this.wires?.replaceChildren(); this.controller = null;
    }
  }
  customElements.define('bc-growth-system', BlackCometGrowthSystem);
})();
